/**
 * Live option data for the session appointment form outside the provider
 * calendar page (in-session scheduler). Wires the same catalogs, care
 * team, and room availability the calendar uses into the shared pure builders.
 */

import { useMemo } from 'react';
import type { SessionAppointmentFieldsValue } from './SessionAppointmentFormFields';
import { useCalendarAppointmentFormOptions } from './useCalendarAppointmentFormOptions';
import { useCalendarPractitionerAccess } from './useCalendarPractitionerAccess';
import {
  attendeeLabelMap,
  buildEpisodeOptions,
  buildSessionAttendeeOptions,
  buildSessionProviderOptions,
  buildSessionTypeOptions,
  resolveSchedulingSiteId,
  sessionTypeDurationMap,
  type SelectOption,
} from './sessionSchedulerOptions';
import { useLocationResources } from '@/hooks/useLocationResources';
import { useRoomAvailabilityForDate } from '@/hooks/useRoomAvailabilityForDate';
import {
  useProviderSchedulableSessionTypes,
  type AppointmentSessionType,
} from '@/pages/calendar/appointmentTypeCatalog';
import {
  filterSessionTypesForEpisodeType,
  useEpisodeOfCareTypes,
} from '@/pages/calendar/episodeOfCareTypeCatalog';
import { buildCalendarStatusSelectData } from '@/pages/appointments/utils/appointmentWorkflowStatus';
import { CALENDAR_CLIENT_ATTENDANCE_OPTIONS } from '@/lib/calendarClientAttendanceStatus';
import type { MinuteInterval } from '@/pages/calendar/components/roomSchedulingDemoData';
import type { Equipment } from '@/pages/settings/components/EquipmentManagementSection';
import type { Room } from '@/pages/settings/components/RoomManagementSection';

export interface UseSessionSchedulerOptionsArgs {
  /** Skip all loading until the scheduler is actually shown. */
  enabled: boolean;
  organizationId: string;
  viewerPractitionerId: string;
  values: SessionAppointmentFieldsValue;
  /** Appointment being rescheduled; excluded from room/device conflict checks. */
  existingAppointmentId?: string;
  savedAttendanceStatus?: string;
  editing?: boolean;
  /** Stored session-type label when the viewer's catalog does not include it. */
  selectedSessionTypeLabel?: string;
}

export interface UseSessionSchedulerOptionsResult {
  episodeOptions: SelectOption[];
  episodesLoading: boolean;
  episodeDetails: ReturnType<typeof useCalendarAppointmentFormOptions>['liveEpisodeDetails'];
  providerOptions: SelectOption[];
  providersLoading: boolean;
  sessionTypeOptions: SelectOption[];
  attendeeOptions: SelectOption[];
  attendeeLabelByRef: Map<string, string>;
  statusOptions: { value: string; label: string; disabled?: boolean }[];
  sites: ReturnType<typeof useLocationResources>['sites'];
  siteOptions: SelectOption[];
  firstSiteId: string;
  rooms: Room[];
  portableDevices: Equipment[];
  fixedInRoom: Equipment[];
  busyByRoom: Map<string, MinuteInterval[]> | undefined;
  busyByDevice: Map<string, MinuteInterval[]> | undefined;
  /** Unfiltered catalog for the selected provider — id lookups (CPT, duration). */
  allSessionTypes: AppointmentSessionType[];
  /** Types approved for the selected episode's therapy type. */
  episodeFilteredTypes: AppointmentSessionType[];
  /** Episode type's standard session type, used to default a new appointment. */
  standardSessionTypeId: string | undefined;
  sessionTypeDurations: Map<string, number>;
  /** Preferred-only display for the currently selected provider. */
  providerPreferredDisplay: string | undefined;
  /** Preferred-only Practitioner labels for FHIR writes. */
  practitionerPreferredDisplayById: Map<string, string>;
  /** Selected episode row, when it is on the viewer's care-managed list. */
  selectedEpisode: ReturnType<
    typeof useCalendarAppointmentFormOptions
  >['liveEpisodeDetails'][number] | null;
  viewerPreferredDisplay: string | undefined;
}

export function useSessionSchedulerOptions(
  args: UseSessionSchedulerOptionsArgs,
): UseSessionSchedulerOptionsResult {
  const {
    enabled,
    organizationId,
    viewerPractitionerId,
    values,
    existingAppointmentId,
    savedAttendanceStatus,
    editing,
    selectedSessionTypeLabel,
  } = args;

  const formOptions = useCalendarAppointmentFormOptions(enabled, values.provider || undefined);
  const { accessById, sessionTypeScope } = useCalendarPractitionerAccess();

  const canManageByOwnerId = useMemo(
    () => new Map([...accessById].map(([id, access]) => [id, access.canManage])),
    [accessById],
  );

  const selectedEpisode = useMemo(
    () => formOptions.liveEpisodeDetails.find((e) => e.id === values.episodeOfCareId) ?? null,
    [formOptions.liveEpisodeDetails, values.episodeOfCareId],
  );

  const allSessionTypes = useProviderSchedulableSessionTypes(
    organizationId,
    values.provider || viewerPractitionerId,
    sessionTypeScope,
  );
  const { types: episodeOfCareTypes } = useEpisodeOfCareTypes();
  const selectedEocType = useMemo(
    () => episodeOfCareTypes.find((t) => t.code && t.code === selectedEpisode?.typeCode),
    [episodeOfCareTypes, selectedEpisode],
  );
  const episodeFilteredTypes = useMemo(
    () => filterSessionTypesForEpisodeType(allSessionTypes, selectedEocType),
    [allSessionTypes, selectedEocType],
  );

  const { sites, roomsBySiteId, equipmentBySiteId } = useLocationResources({
    organizationId,
  });
  const siteOptions = useMemo(
    () =>
      sites.map((s) => ({
        value: s.id,
        label: [s.name, s.addressLine1].filter(Boolean).join(' — '),
      })),
    [sites],
  );
  const firstSiteId = sites[0]?.id ?? '';

  const schedulingSiteId = resolveSchedulingSiteId(values);
  const rooms = useMemo(
    () => roomsBySiteId[schedulingSiteId] ?? [],
    [roomsBySiteId, schedulingSiteId],
  );
  const portableDevices = useMemo(
    () =>
      (equipmentBySiteId[schedulingSiteId] ?? []).filter(
        (e) => e.portability === 'portable' && e.status === 'active',
      ),
    [equipmentBySiteId, schedulingSiteId],
  );
  const fixedInRoom = useMemo(
    () =>
      (equipmentBySiteId[schedulingSiteId] ?? []).filter(
        (e) => e.portability === 'fixed' && e.homeRoomId === values.roomId && e.status === 'active',
      ),
    [equipmentBySiteId, schedulingSiteId, values.roomId],
  );

  const { busyByRoom, busyByDevice } = useRoomAvailabilityForDate({
    date: enabled ? values.date : null,
    roomIds: rooms.map((r) => r.id),
    deviceIds: portableDevices.map((d) => d.id),
    excludeAppointmentId: existingAppointmentId,
    refreshKey: enabled,
  });

  const episodeOptions = useMemo(
    () =>
      buildEpisodeOptions({
        episodeSelectData: formOptions.episodeSelectData,
        episodeDetails: formOptions.liveEpisodeDetails,
        selectedEpisodeId: values.episodeOfCareId,
        fallbackLabel: values.client ?? undefined,
      }),
    [
      formOptions.episodeSelectData,
      formOptions.liveEpisodeDetails,
      values.episodeOfCareId,
      values.client,
    ],
  );

  const providerOptions = useMemo(
    () =>
      buildSessionProviderOptions({
        canManageByOwnerId,
        selectedProviderId: values.provider,
        practitionerDisplayById: formOptions.practitionerDisplayById,
      }),
    [canManageByOwnerId, values.provider, formOptions.practitionerDisplayById],
  );

  const sessionTypeOptions = useMemo(
    () =>
      buildSessionTypeOptions({
        episodeFilteredTypes,
        allProviderTypes: allSessionTypes,
        selectedSessionTypeId: values.sessionType,
        editing,
        fallbackLabel: selectedSessionTypeLabel,
      }),
    [episodeFilteredTypes, allSessionTypes, values.sessionType, editing, selectedSessionTypeLabel],
  );

  const attendeeOptions = useMemo(
    () =>
      buildSessionAttendeeOptions({
        episode: selectedEpisode,
        selectedRefs: values.sessionAttendees,
      }),
    [selectedEpisode, values.sessionAttendees],
  );

  const statusOptions = useMemo(
    () =>
      buildCalendarStatusSelectData(
        savedAttendanceStatus ?? values.attendanceStatus,
        CALENDAR_CLIENT_ATTENDANCE_OPTIONS,
      ),
    [savedAttendanceStatus, values.attendanceStatus],
  );

  const providerPreferredDisplay = values.provider
    ? formOptions.practitionerPreferredDisplayById.get(values.provider)
    : undefined;

  return {
    episodeOptions,
    episodesLoading: formOptions.episodesLoading,
    episodeDetails: formOptions.liveEpisodeDetails,
    providerOptions,
    providersLoading: formOptions.loading,
    sessionTypeOptions,
    attendeeOptions,
    attendeeLabelByRef: attendeeLabelMap(attendeeOptions),
    statusOptions,
    sites,
    siteOptions,
    firstSiteId,
    rooms,
    portableDevices,
    fixedInRoom,
    busyByRoom,
    busyByDevice,
    allSessionTypes,
    episodeFilteredTypes,
    standardSessionTypeId: selectedEocType?.standardSessionTypeId ?? undefined,
    sessionTypeDurations: sessionTypeDurationMap(allSessionTypes),
    providerPreferredDisplay,
    practitionerPreferredDisplayById: formOptions.practitionerPreferredDisplayById,
    selectedEpisode,
    viewerPreferredDisplay:
      formOptions.practitionerPreferredDisplayById.get(viewerPractitionerId)
      ?? undefined,
  };
}
